import React, { useMemo, useState } from "react";
import { Offcanvas, Button, Form, Row, Col } from "react-bootstrap";
import type { DashboardDisposition } from "../../../hooks/dashboard/useDashboard";

type Props = {
  show: boolean;
  handleClose: () => void;
  handleAddWidget: (content: string, type: string) => Promise<void> | void;
  onApplyDisposition?: (disposition: DashboardDisposition) => void;
};

type WidgetType = "alerts" | "fleet_state" | "fleet_counter" | "fleet_co2" | "stats";

const WIDGET_TYPES: Array<{ value: WidgetType; label: string }> = [
  { value: "fleet_counter", label: "Compteur" },
  { value: "fleet_state", label: "Etat flotte" },
  { value: "alerts", label: "Alertes" },
  { value: "fleet_co2", label: "CO2" },
  { value: "stats", label: "Vitesse" },
];

const SECTION_TYPES: Array<{ value: DashboardDisposition; label: string; bars: number[] }> = [
  { value: "one", label: "1 colonne", bars: [12] },
  { value: "two", label: "2 colonnes", bars: [6, 6] },
  { value: "three", label: "3 colonnes", bars: [4, 4, 4] },
  { value: "four", label: "4 colonnes", bars: [3, 3, 3, 3] },
  { value: "six", label: "6 colonnes", bars: [2, 2, 2, 2, 2, 2] },
  { value: "70-30", label: "70 / 30", bars: [8, 4] },
  { value: "30-70", label: "30 / 70", bars: [4, 8] },
  { value: "25-50-25", label: "25 / 50 / 25", bars: [3, 6, 3] },
  { value: "20-60-20", label: "20 / 60 / 20", bars: [2, 8, 2] },
];

const initialForm = {
  numberOfItem: "42",
  title: "Vehicles",
  icon: "car",
  color: "bg-primary",
  linkTo: "/vehicles",
  drivingValue: "5",
  parkingValue: "10",
  parkingEngineRunningValue: "2",
  lastTransmissionValue: "0",
  psn: "",
};

const widgetIconByType: Record<WidgetType, string> = {
  fleet_counter: "ri-dashboard-3-line",
  fleet_state: "ri-pie-chart-2-line",
  alerts: "ri-notification-3-line",
  fleet_co2: "ri-bar-chart-2-line",
  stats: "ri-speed-up-line",
};

export default function AddWidgetModal({ show, handleClose, handleAddWidget, onApplyDisposition }: Props) {
  const [type, setType] = useState<WidgetType>("fleet_counter");
  const [form, setForm] = useState(initialForm);

  const canSubmit = useMemo(() => type.trim().length > 0, [type]);

  const updateField = (field: keyof typeof initialForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildContent = () => {
    switch (type) {
      case "alerts":
        return { Notifications: [] };
      case "fleet_state":
        return {
          DrivingValue: Number(form.drivingValue || 0),
          ParkingValue: Number(form.parkingValue || 0),
          ParkingEngineRunningValue: Number(form.parkingEngineRunningValue || 0),
          LastTransmissionValue: Number(form.lastTransmissionValue || 0),
        };
      case "fleet_co2":
        return { data: {} };
      case "stats":
        return { psn: form.psn.trim() };
      case "fleet_counter":
      default:
        return {
          numberOfItem: Number(form.numberOfItem || 0),
          title: form.title.trim() || "Counter",
          icon: form.icon.trim() || "car",
          color: form.color,
          linkTo: form.linkTo.trim() || "/vehicles",
        };
    }
  };

  const onSubmit = async () => {
    if (!canSubmit) return;
    await handleAddWidget(JSON.stringify(buildContent()), type);
    setType("fleet_counter");
    setForm(initialForm);
  };

  return (
    <Offcanvas show={show} onHide={handleClose} placement="end" className="dash2-add-builder-drawer">
      <Offcanvas.Header closeButton>
        <div>
          <Offcanvas.Title>Ajouter un élément</Offcanvas.Title>
          <div className="dash2-modal-subtitle">Sélectionne une disposition ou un widget à ajouter au dashboard.</div>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <div className="dash2-modal-section">
          <div className="dash2-modal-title">Sections / Colonnes</div>
          <div className="dash2-modal-help">Choisis une disposition pour créer une nouvelle rangée vide.</div>
          <div className="dash2-card-grid">
            {SECTION_TYPES.map((section) => (
              <button
                type="button"
                key={section.value}
                className="dash2-builder-card"
                onClick={() => {
                  onApplyDisposition?.(section.value);
                  handleClose();
                }}
              >
                <span className="dash2-column-icon">
                  {section.bars.map((bar, index) => (
                    <span key={`${section.value}-${index}`} style={{ flex: bar }} />
                  ))}
                </span>
                <strong>{section.label}</strong>
                <small>Créer cette rangée</small>
              </button>
            ))}
          </div>
        </div>

        <div className="dash2-modal-section">
          <div className="dash2-modal-title">Widgets</div>
          <div className="dash2-modal-help">Choisis un widget, règle ses options, puis ajoute-le au builder.</div>
          <div className="dash2-card-grid dash2-widget-card-grid">
            {WIDGET_TYPES.map((item) => (
              <button
                type="button"
                key={item.value}
                className={`dash2-builder-card ${type === item.value ? "active" : ""}`}
                onClick={() => setType(item.value)}
              >
                <span className="dash2-content-icon">
                  <i className={widgetIconByType[item.value]} />
                </span>
                <strong>{item.label}</strong>
                <small>Ajouter ce widget</small>
              </button>
            ))}
          </div>
        </div>

        <div className="dash2-widget-config">
          <div className="dash2-modal-title">Options du widget</div>

        {type === "fleet_counter" && (
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>Titre</Form.Label>
              <Form.Control value={form.title} onChange={(e) => updateField("title", e.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Valeur</Form.Label>
              <Form.Control type="number" value={form.numberOfItem} onChange={(e) => updateField("numberOfItem", e.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Icone</Form.Label>
              <Form.Control value={form.icon} onChange={(e) => updateField("icon", e.target.value)} placeholder="car, user-tie, bell..." />
            </Col>
            <Col md={6}>
              <Form.Label>Couleur</Form.Label>
              <Form.Select value={form.color} onChange={(e) => updateField("color", e.target.value)}>
                <option value="bg-primary">Bleu</option>
                <option value="bg-info-light">Info</option>
                <option value="bg-danger-light">Rouge</option>
                <option value="bg-success-light">Vert</option>
                <option value="bg-success-user">Utilisateur</option>
              </Form.Select>
            </Col>
            <Col md={12}>
              <Form.Label>Lien</Form.Label>
              <Form.Control value={form.linkTo} onChange={(e) => updateField("linkTo", e.target.value)} />
            </Col>
          </Row>
        )}

        {type === "fleet_state" && (
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>En route</Form.Label>
              <Form.Control type="number" value={form.drivingValue} onChange={(e) => updateField("drivingValue", e.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>En parking</Form.Label>
              <Form.Control type="number" value={form.parkingValue} onChange={(e) => updateField("parkingValue", e.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Moteur en marche</Form.Label>
              <Form.Control type="number" value={form.parkingEngineRunningValue} onChange={(e) => updateField("parkingEngineRunningValue", e.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Sans transmission</Form.Label>
              <Form.Control type="number" value={form.lastTransmissionValue} onChange={(e) => updateField("lastTransmissionValue", e.target.value)} />
            </Col>
          </Row>
        )}

        {type === "stats" && (
          <Form.Group>
            <Form.Label>PSN dispositif</Form.Label>
            <Form.Control value={form.psn} onChange={(e) => updateField("psn", e.target.value)} />
          </Form.Group>
        )}
        </div>
      </Offcanvas.Body>

      <div className="dash2-drawer-footer">
        <Button variant="secondary" onClick={handleClose}>
          Annuler
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={!canSubmit}>
          Ajouter
        </Button>
      </div>
    </Offcanvas>
  );
}
